import {inject, injectable} from "inversify";
import {BankCategory} from "@/modules/finances/enums";
import {EventEmitter} from "@/modules/base/events";
import {Season} from "@/modules/season/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {Stadium, Terrace} from "@/modules/stadium/entity";
import {BeforeFoundationFinishedEvent} from "@/modules/stadium/events/BeforeFoundationFinishedEvent";
import {AfterFoundationFinishedEvent} from "@/modules/stadium/events/AfterFoundationFinishedEvent";
import {FoundationDefinitionType} from "@/modules/stadium/types";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

@injectable()
export default class StadiumFoundationExpander {
  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async finish(stadium: Stadium) {
    if (!stadium.foundationFinishedAt) {
      throw 'There is not expansion planed for this stadium.';
    }

    const season = await Season.current();

    if (stadium.foundationFinishedAt > season.currentDateRaw) {
      throw 'Expansion is not finished.';
    }

    await this.em.emitAsync(new BeforeFoundationFinishedEvent(stadium));

    stadium.foundationLevel++;
    stadium.foundationFinishedAt = null;
    await stadium.save();

    const definition: FoundationDefinitionType = this.configRepository.get('stadium.foundations')[stadium.foundationLevel - 1];

    await stadium.terraces.forEachAsync(async (terrace: Terrace) => {
      terrace.available = definition[terrace.type].available ?? false;
      terrace.seatsLimit = definition[terrace.type].limit ?? 0;
      await terrace.save();
    });

    await this.em.emitAsync(new AfterFoundationFinishedEvent(stadium));
  }

  public async queue(stadium: Stadium) {
    if (stadium.foundationFinishedAt) {
      throw 'Stadium foundation is expansion is ongoing.';
    }

    const season = await Season.current();
    const duration = this.configRepository.get('stadium.durations.foundation')(stadium);
    const price = this.configRepository.get('stadium.costs.foundation')(stadium);

    await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
      price,
      {
        category: BankCategory.STADIUM,
        label: translator.t('stadium_foundation_expansion'),
      }
    );

    stadium.foundationFinishedAt = season.currentDate.add(duration, 'days').toDate();
    await stadium.save();
  }
}
